import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Filter values that survive leaving and coming back to a page (stored per browser).
 * Returns the values, a setter for one filter, a reset, and how many differ from the defaults.
 */
export function usePersistentFilters<T extends Record<string, string>>(key: string, defaults: T) {
  const storageKey = `crm.filters.${key}`;
  const [values, setValues] = useState<T>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
      if (saved && typeof saved === "object") return { ...defaults, ...saved };
    } catch {
      // Storage unavailable (private mode) or corrupt: start from defaults
    }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(values));
    } catch {
      // Not critical
    }
  }, [storageKey, values]);

  const set = useCallback((name: keyof T, value: string) => setValues((v) => ({ ...v, [name]: value })), []);
  const reset = useCallback(() => setValues(defaults), [defaults]);
  const activeCount = useMemo(
    () => (Object.keys(defaults) as (keyof T)[]).filter((k) => values[k] !== defaults[k]).length,
    [values, defaults]
  );

  return { values, set, reset, activeCount };
}
